import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { FilterMenuGroup } from '../../models/filter-menu-group.model';
import { FilterMenuGroupOption } from '../../models/filter-menu-group-option.model';
import { MatAccordion } from '@angular/material/expansion';
import { FilterStateService } from '../../services/filter-state.service';

@Component({
  standalone: false,
  selector: 'app-filter-menu',
  templateUrl: './filter-menu.component.html',
  styleUrls: ['./filter-menu.component.scss'],
})
export class FilterMenuComponent implements OnInit, OnChanges {
  @Output() applyFilters = new EventEmitter<FilterMenuGroup[]>();
  @Input() trigger!: MatMenuTrigger;
  @Input() filterGroups!: FilterMenuGroup[];
  @Input() contextKey?: string; // Add context key to differentiate between pages

  @ViewChild(MatAccordion, { static: false }) accordion!: MatAccordion;

  groupsFiltersSelectedCount?: Map<string, number>;
  filtersSelectedCount: number = 0;
  selectAllMap: Map<string, boolean> = new Map<string, boolean>();

  constructor(private filterStateService: FilterStateService) {}

  ngOnInit() {
    this.restoreSavedFilters();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filterGroups'] && changes['filterGroups'].currentValue) {
      this.restoreSavedFilters();
    }
  }

  filterOptionToggle(option: FilterMenuGroupOption, groupTitle: string) {
    option.selected = !option.selected;
    this.buildFiltersCounters();
    this.updateGroupSelectAllState(groupTitle);
  }

  toggleGroupSelectAll(group: FilterMenuGroup) {
    const currentState = this.selectAllMap.get(group.title) || false;
    const newState = !currentState;
    this.selectAllMap.set(group.title, newState);

    group.options.forEach((option) => (option.selected = newState));
    this.buildFiltersCounters();
  }

  applyFiltersClick() {
    const groupsFiltersWithSelection = this.filterGroups.filter(
      (group) => group.options.filter((option) => option.selected).length > 0
    );
    this.filterStateService.setFilters(this.filterGroups, this.contextKey);
    this.applyFilters.emit(groupsFiltersWithSelection);
    this.close();
  }

  clearFilter() {
    this.filterGroups.forEach((group) => {
      group.options.forEach((option) => (option.selected = false));
      this.selectAllMap.set(group.title, false);
    });
    this.buildFiltersCounters();
    this.filterStateService.clearFilters(this.contextKey);
    this.applyFiltersClick();
  }

  close() {
    this.trigger.closeMenu();
  }

  closeAllAccordions() {
    this.accordion.closeAll();
  }

  private buildFiltersCounters() {
    if (!this.filterGroups) return;

    this.filtersSelectedCount = 0;
    this.groupsFiltersSelectedCount = new Map<string, number>();

    this.filterGroups.forEach((group) => {
      const selectedCount = group.options.filter(
        (option) => option.selected
      ).length;
      this.groupsFiltersSelectedCount!.set(group.title, selectedCount);
      this.filtersSelectedCount += selectedCount;

      this.updateGroupSelectAllState(group.title);
    });
  }

  private updateGroupSelectAllState(groupTitle: string) {
    const group = this.filterGroups.find((g) => g.title === groupTitle);
    if (!group) return;

    const allSelected = group.options.every((option) => option.selected);
    this.selectAllMap.set(groupTitle, allSelected);
  }

  private restoreSavedFilters() {
    const savedGroups = this.filterStateService.getFilters(this.contextKey);
    let anyRestored = false;
    if (savedGroups && savedGroups.length > 0 && this.filterGroups) {
      this.filterGroups.forEach((group) => {
        const savedGroup = savedGroups.find(g => g.title === group.title);
        if (savedGroup) {
          group.options.forEach(option => {
            const savedOption = savedGroup.options.find(o => o.title === option.title);
            if (savedOption) {
              option.selected = savedOption.selected;
              if (savedOption.selected) anyRestored = true;
            }
          });
        }
      });
      this.buildFiltersCounters();
      if (anyRestored) {
        const groupsFiltersWithSelection = this.filterGroups.filter(
          (group) => group.options.filter((option) => option.selected).length > 0
        );
        this.applyFilters.emit(groupsFiltersWithSelection);
      }
    }
  }
}
